import type { GridInterface } from '@ad-lucy/context/adData';

import type { GridDefinition, KnownGridNames } from '../types';

/**
 * Converts a grid definition into a full-fledged Grid object.
 *
 * This mainly involves adding some additional calculated bits which
 * would be obnoxious to re-define for every newly added
 * grid definition, when it can be done automatically instead.
 *
 * To see what this does, simply compare the "GridDefinition" type to the "GridInterface" type.
 *
 * @param name The name of the grid, which will be added to each subgrid's name
 * @param grids The grid definition to transform
 * @param maxRows The maximum number of rows to allow in the grid
 * @param maxCols The maximum number of columns to allow in the grid
 */
export const defineGrid = (
  name: KnownGridNames,
  grids: GridDefinition[],
  maxRows?: number,
  maxCols?: number,
): GridInterface[] => {
  return grids.map((grid): GridInterface => {
    // Calculate the number of rows in the grid by adding all the rows together.
    // The rows have to be counted by their row span, which can exceed their count.
    const numRows = grid.rows.reduce<number>((num, row) => {
      return num + Math.max(...row.columns.map((column) => column.spanRow ?? 1));
    }, 0);

    // Calculate the number of columns in the grid by adding all the columns together.
    // The columns have to be counted by their column span, which can exceed their count.
    const numCols = Math.max(
      ...grid.rows.map((row) =>
        row.columns.reduce<number>((num, col) => {
          return num + (col.spanCol ?? 1);
        }, 0),
      ),
    );

    return {
      ...grid,
      name: `${name} (${grid.name})`,
      numRows: maxRows && numRows > maxRows ? maxRows : numRows,
      numCols: maxCols && numCols > maxCols ? maxCols : numCols,
      rows: grid.rows.map((row) => {
        return {
          ...row,
          columns: row.columns.map((column) => {
            return {
              ...column,
              spanRow: column.spanRow ?? 1,
              spanCol: column.spanCol ?? 1,
            };
          }),
        };
      }),
    };
  });
};
